import { LogContext } from './log-error.interface';

// Simple in-memory cache for deduplication to prevent flooding
const errorCache = new Set<string>();
const DEDUPE_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Generates a minimal, safe hash for deduplication based on error message and context.
 */
function getErrorHash(error: unknown, context: LogContext): string {
  const errMsg = error instanceof Error ? error.message : String(error);
  const contextStr = context.context ?? '';
  return `${errMsg}:${contextStr}:${context.category}`;
}

/**
 * Safely strips query parameters from a URL to avoid leaking PII via query strings.
 */
function stripUrlQuery(urlStr?: string): string | undefined {
  if (!urlStr) return undefined;
  try {
    const url = new URL(urlStr);
    return `${url.origin}${url.pathname}`;
  } catch {
    // Fallback if URL parsing fails (e.g., relative path)
    return urlStr.split('?')[0];
  }
}

/**
 * Centralized utility to log errors gracefully and securely.
 * Designed as a thin wrapper around a third-party telemetry service (e.g., Sentry).
 * 
 * @param error The Error object or string caught in the catch block.
 * @param context Required typed metadata (LogContext) strictly defining the error taxonomy.
 */
export function logError(error: unknown, context: LogContext): void {
  try {
    // 1. Deduplication (Throttle)
    const hash = getErrorHash(error, context);
    if (errorCache.has(hash)) {
      return; // Skip logging if we've seen this exact error recently
    }
    
    // Add to cache and set timeout to clear it
    errorCache.add(hash);
    setTimeout(() => {
      errorCache.delete(hash);
    }, DEDUPE_TIMEOUT_MS);

    // 2. Build the Allowlisted Payload (Minimal, typed collection)
    // We strictly only collect these fields. We never blindly attach full request/form bodies.
    const safePayload = {
      message: error instanceof Error ? error.message : String(error),
      name: error instanceof Error ? error.name : 'UnknownError',
      category: context.category,
      correlationId: context.correlationId,
      componentStack: context.componentStack,
      status: context.status,
      url: stripUrlQuery(context.url ?? (typeof window !== 'undefined' ? window.location.href : undefined)),
      contextString: context.context,
      timestamp: new Date().toISOString()
      // Note: app version can be injected here automatically from env vars
    };

    const isDevelopment = process.env.NODE_ENV === 'development';

    if (isDevelopment) {
      // 3. Development Behavior: Direct console output
      console.group('%c 🛑 Application Error', 'color: white; background: red; padding: 2px 4px; border-radius: 4px;');
      console.error(error);
      console.log('Safe Payload:', safePayload);
      console.groupEnd();
    } else {
      // 4. Production Behavior: Telemetry logging
      // TODO: Actual Sentry / Telemetry initialization (See SIR-0012 for integration plan)
      
      // Temporarily outputting to console in production to prevent swallowing 
      // until the Sentry SDK is fully integrated and tested.
      console.error('Production Error Intercepted (Telemetry Pending):', error, safePayload);
    }
  } catch (loggingError) {
    // 5. Fail-Safe: If the logger itself fails, it MUST NOT throw and crash the application.
    // We log it even in production so failures aren't swallowed invisibly (ERR-003).
    console.error('Logger Utility Failed:', loggingError);
  }
}
